import type { PostOfficeContextValue } from '@atlassian/post-office-context/types';
import type { PostOfficeEnvironmentConfig } from '@atlassian/post-office-context/post-office/types';

import { isStableUIEnabledForTests } from '@confluence/e2e-url-override/entry-points/isStableUIEnabledForTests.ts';
import { type SessionDataType } from '@confluence/session-data/entry-points/SessionDataTypes.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';

const CONFLUENCE_PRODUCT = 'confluence';

const DEFAULT_POST_OFFICE_CONTEXT = {
	product: CONFLUENCE_PRODUCT,
	locale: 'en',
};

const getWorkspaceARI = ({ cloudId, activationId }: { cloudId: string; activationId: string }) => {
	return `ari:cloud:confluence:${cloudId}:workspace/${activationId}`;
};

export type ConfluenceSessionDataFields =
	| 'accessStatus'
	| 'activationId'
	| 'cloudId'
	| 'locale'
	| 'orgId'
	| 'edition';

export const sessionToPostOfficeContext = (
	sessionData: Pick<SessionDataType, ConfluenceSessionDataFields>,
): PostOfficeContextValue => {
	const { accessStatus, activationId, cloudId, locale, orgId, edition } = sessionData;

	const isAdmin = accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS;
	const workspaceAri = getWorkspaceARI({ activationId, cloudId });

	return {
		...DEFAULT_POST_OFFICE_CONTEXT,
		locale,
		orgId,
		isSiteAdmin: isAdmin,
		tenantId: cloudId === '' ? undefined : cloudId,
		workspaceAri,
		workspaceId: activationId,
		subproduct: undefined,
		productEdition: edition || undefined,
		disableAllPlacements: isStableUIEnabledForTests() ? true : undefined,
	};
};

export const transformConfluenceEnvToPostOfficeEnvironment = (
	environment: SessionDataType['environment'] | null,
): PostOfficeEnvironmentConfig['env'] => {
	switch (environment) {
		case 'STAGING':
			return 'staging';
		case 'PRODUCTION':
			return 'production';
		case 'DEVELOPMENT':
			return 'local';
		default:
			return 'local';
	}
};
